import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const input = "h-8 w-full min-w-0 rounded-lg border border-white/10 bg-white/[0.04] px-2 text-xs text-white focus:outline-none focus:ring-2 focus:ring-cyan-400/50";
const btn = "rounded-full border border-cyan-400/40 px-3 py-1 text-xs font-semibold text-cyan-200 hover:bg-cyan-500/10";
const save = (p) => p.then(() => toast.success("Saved")).catch((e) => toast.error(e.response?.data?.detail?.toString() || "Save failed"));

const PriceRow = ({ pb }) => {
  const [amount, setAmount] = useState(pb.amount_minor / 100);
  const [hosting, setHosting] = useState(pb.optional_monthly_hosting_minor ? pb.optional_monthly_hosting_minor / 100 : "");
  const intl = pb.region === "INTL";
  const fmt = (v) => `${intl ? "US$" : "₹"}${Number(v).toLocaleString(intl ? "en-US" : "en-IN", Number(v) % 1 ? { minimumFractionDigits: 2, maximumFractionDigits: 2 } : {})}`;
  const submit = () => save(api.patch(`/admin/price-books/${pb.id}`, {
    amount_minor: Math.round(Number(amount) * 100), display_price: fmt(amount),
    ...(hosting !== "" && { optional_monthly_hosting_minor: Math.round(Number(hosting) * 100), optional_monthly_hosting_display: `${fmt(hosting)}/mo` }) }));
  return (
    <tr className="border-t border-white/5" data-testid={`admin-price-row-${pb.product_key}-${pb.region}`}>
      <td className="py-2 pr-2">{pb.product_key}</td><td className="pr-2">{pb.region} · {pb.currency}</td>
      <td className="pr-2"><input className={input} type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} aria-label="Amount" data-testid={`admin-price-input-${pb.product_key}-${pb.region}`} /></td>
      <td className="pr-2">{pb.product_key === "DFY_WEBSITE_LAUNCH" ? <input className={input} type="number" min="0" value={hosting} onChange={(e) => setHosting(e.target.value)} aria-label="Hosting per month" /> : "—"}</td>
      <td><button type="button" className={btn} onClick={submit} data-testid={`admin-price-save-${pb.product_key}-${pb.region}`}>Save</button></td>
    </tr>
  );
};

const GrowthCap = ({ products }) => {
  const growth = products.find((p) => p.key === "GROWTH_DOSSIER");
  const [cap, setCap] = useState(growth?.assessment_quota ?? "");
  return (
    <div className="flex flex-wrap items-end gap-2" data-testid="admin-growth-cap">
      <label className="text-xs text-slate-300">Growth assessment cap (blank = unlimited)
        <input className={`${input} mt-1 w-40`} type="number" min="0" value={cap} onChange={(e) => setCap(e.target.value)} data-testid="admin-growth-cap-input" />
      </label>
      <button type="button" className={btn} data-testid="admin-growth-cap-save"
        onClick={() => save(api.patch("/admin/products/GROWTH_DOSSIER", { assessment_quota: cap === "" ? null : Number(cap) }))}>Save</button>
    </div>
  );
};

const StatEditor = () => {
  const [stat, setStat] = useState(null);
  useEffect(() => { api.get("/settings/public").then((r) => setStat(r.data.execution_window_stat)); }, []);
  if (!stat) return null;
  const set = (k) => (e) => setStat({ ...stat, [k]: e.target.value });
  return (
    <div className="grid gap-2" data-testid="admin-stat-editor">
      <textarea className={`${input} h-20 py-1`} value={stat.description} onChange={set("description")} aria-label="Benchmark text" data-testid="admin-stat-description" />
      <input className={input} value={stat.source_label} onChange={set("source_label")} aria-label="Source label" data-testid="admin-stat-source" />
      <button type="button" className={`${btn} justify-self-start`} data-testid="admin-stat-save"
        onClick={() => save(api.patch("/admin/settings/execution_window_stat", { description: stat.description, source_label: stat.source_label }))}>Save</button>
    </div>
  );
};

const Orders = ({ orders, reload }) => (
  <div className="noscrollbar overflow-x-auto">
    <table className="w-full min-w-[760px] text-left text-xs text-slate-300" data-testid="admin-orders-table">
      <thead className="text-slate-500"><tr><th>Date</th><th>Status</th><th>Product</th><th>User</th><th>Provider</th><th>Amount</th><th>DFY</th></tr></thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id} className="border-t border-white/5 align-top" data-testid={`admin-order-${o.id}`}>
            <td className="py-2 pr-2">{String(o.created_at).slice(0, 16).replace("T", " ")}</td>
            <td className="pr-2 font-semibold">{o.status}</td><td className="pr-2">{o.product_key}</td>
            <td className="pr-2">{o.customer_email}<br />{o.customer_mobile || "—"}</td>
            <td className="pr-2">{o.provider}{o.test_mode ? " · TEST" : ""}</td>
            <td className="pr-2">{o.currency} {(o.amount_minor / 100).toLocaleString()}</td>
            <td>{o.dfy ? (
              <span className="flex flex-col gap-1">{o.dfy.desired_domain || "awaiting details"}
                <select className={input} value={o.dfy.status} aria-label="Deployment status" data-testid={`admin-dfy-status-${o.id}`}
                  onChange={(e) => save(api.patch(`/admin/deployments/${o.dfy.id}`, { status: e.target.value })).then(reload)}>
                  {["awaiting_details", "queued", "in_progress", "live"].map((s) => <option key={s} value={s} disabled={s === "awaiting_details"}>{s}</option>)}
                </select></span>
            ) : "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Panel = ({ title, children, testid }) => (
  <section className="iv-glass space-y-3 rounded-2xl p-5" data-testid={testid}><h2 className="text-base font-bold text-white md:text-lg">{title}</h2>{children}</section>
);

export default function AdminPage() {
  const { user, authChecked } = useApp();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => Promise.all([api.get("/admin/overview"), api.get("/admin/price-books"), api.get("/admin/orders")])
    .then(([o, p, r]) => setData({ overview: o.data, prices: p.data, orders: r.data })).catch(() => setDenied(true)), []);
  useEffect(() => { if (authChecked) (user?.role === "admin" ? load() : setDenied(true)); }, [authChecked, user, load]);
  if (denied) return <main className="grid min-h-screen place-items-center bg-[#060913] text-slate-300" data-testid="admin-denied">Admins only.</main>;
  if (!data) return <main className="min-h-screen bg-[#060913]" data-testid="admin-loading" />;
  return (
    <main className="min-h-screen bg-[#060913] px-4 py-8 sm:px-8" data-testid="admin-page">
      <div className="mx-auto max-w-6xl space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Admin</h1>
          <a href="/" className="text-sm font-semibold text-cyan-300">← Workspace</a>
        </div>
        <Panel title="Subscription model · what we offer" testid="admin-offer-summary">
          <ul className="space-y-2 text-sm text-slate-300">{data.overview.offer_summary.map((o) => <li key={o.product_key}><b className="text-white">{o.product_key}</b> · {o.model} — {o.summary}</li>)}</ul>
          <p className="text-xs text-slate-500">{data.overview.pricing_display}. Providers: {data.overview.providers.map((p) => `${p.display_name}: ${p.status}`).join(" · ")}</p>
        </Panel>
        <Panel title="Price books (USD + INR)" testid="admin-price-books">
          <div className="noscrollbar overflow-x-auto"><table className="w-full min-w-[620px] text-left text-xs text-slate-300">
            <thead className="text-slate-500"><tr><th>Product</th><th>Region</th><th>Amount</th><th>Hosting /mo</th><th /></tr></thead>
            <tbody>{data.prices.map((pb) => <PriceRow key={pb.id} pb={pb} />)}</tbody>
          </table></div>
        </Panel>
        <div className="grid gap-5 lg:grid-cols-2">
          <Panel title="Growth tier" testid="admin-growth-panel"><GrowthCap products={data.overview.products} /></Panel>
          <Panel title="Execution Window stat" testid="admin-stat-panel"><StatEditor /></Panel>
        </div>
        <Panel title={`Orders (${data.orders.length})`} testid="admin-orders"><Orders orders={data.orders} reload={load} /></Panel>
      </div>
    </main>
  );
}
